
/* =========================================================
   NYIGEN COLLECTION 3D TILT + FULL CARD CLICK
   ---------------------------------------------------------
   - Desktop Grid tilt only
   - Collection cards clickable everywhere
   - Native Ignite carousel preserved
   - Touch devices supported for clicking
   - Reduced motion respected
   - Shopify editor compatible
   - No external dependencies
   ========================================================= */

(() => {
  "use strict";

  const selector = [
    ".section-collection-list",
    ".collection_list__collections:not(.slider--desktop)",
    ".collection-card.card--media"
  ].join(" ");

  const cardSelector =
    ".section-collection-list .collection-card.card--media";

  const linkSelector =
    "a.collection-card__title[href]";

  const motionQuery = window.matchMedia(
    "(min-width: 990px) and (hover: hover) and " +
    "(pointer: fine) and " +
    "(prefers-reduced-motion: no-preference)"
  );

  const initialized = new WeakSet();

  /* ======================================
     3D TILT
     ====================================== */

  function initializeCard(card) {
    if (initialized.has(card)) return;

    initialized.add(card);

    let frame = 0;
    let x = 0;
    let y = 0;
    let active = false;

    const reset = () => {
      active = false;

      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }

      card.classList.remove("ny3d-active");

      card.style.removeProperty("--ny3d-rx");
      card.style.removeProperty("--ny3d-ry");
      card.style.removeProperty("--ny3d-scale");
    };

    const update = () => {
      frame = 0;

      if (!active || !motionQuery.matches) return;

      card.style.setProperty(
        "--ny3d-rx",
        `${(-y * 6).toFixed(2)}deg`
      );

      card.style.setProperty(
        "--ny3d-ry",
        `${(x * 6).toFixed(2)}deg`
      );

      card.style.setProperty(
        "--ny3d-scale",
        "1.015"
      );
    };

    const move = (event) => {
      if (!motionQuery.matches) {
        reset();
        return;
      }

      const rect = card.getBoundingClientRect();

      if (!rect.width || !rect.height) return;

      x = Math.max(
        -1,
        Math.min(
          1,
          (
            (event.clientX - rect.left) /
            rect.width - 0.5
          ) * 2
        )
      );

      y = Math.max(
        -1,
        Math.min(
          1,
          (
            (event.clientY - rect.top) /
            rect.height - 0.5
          ) * 2
        )
      );

      active = true;
      card.classList.add("ny3d-active");

      if (!frame) {
        frame = requestAnimationFrame(update);
      }
    };

    card.classList.add("ny3d-ready");

    card.addEventListener("pointermove", move, {
      passive: true
    });

    card.addEventListener(
      "pointerleave",
      reset
    );

    card.addEventListener(
      "pointercancel",
      reset
    );

    motionQuery.addEventListener(
      "change",
      reset
    );
  }

  /* ======================================
     INITIALIZE TILT
     ====================================== */

  function initialize(root = document) {
    root.querySelectorAll(selector).forEach(
      initializeCard
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initialize(),
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initialize(event.target);
    }
  );

  /* ======================================
     FULL COLLECTION CARD CLICK
     ====================================== */

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented) return;

    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target;

    if (!(target instanceof Element)) return;

    const card = target.closest(cardSelector);

    if (!card) return;

    // Do not interfere with existing links
    // or interactive controls.
    if (
      target.closest(
        "a, button, input, select, textarea, summary"
      )
    ) {
      return;
    }

    const link = card.querySelector(linkSelector);

    if (!link) return;

    window.location.assign(link.href);
  });

  /* ======================================
     POINTER CURSOR
     ====================================== */

  function setCardCursors(root = document) {
    root.querySelectorAll(cardSelector).forEach(
      (card) => {
        if (card.querySelector(linkSelector)) {
          card.style.cursor = "pointer";
        }
      }
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => setCardCursors(),
      { once: true }
    );
  } else {
    setCardCursors();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      setCardCursors(event.target);
    }
  );
})();
